import { Skeleton, Stack } from "@mui/material";
import {
    canChangeInvestmentStrategySelector,
    editInvestmentStrategyResultSelector,
    questionnaireRejectedSelector,
    setCanChangeInvestmentStrategy,
    setStepByName,
    StepperIndexes,
    useAppDispatch,
    useAppSelector,
} from "@repo/logic";
import { FC, useEffect, useMemo } from "react";
import { useTypedTranslation } from "../../hooks";
import { FormErrorMessage } from "../FormErrorMessage";
import { ProgressBarList } from "../ProgressBarList";
import { PensionQuestionnaireStepProps } from "./PensionQuestionnaireStepper";
import { DefaultStepProps } from "../Stepper/CustomStepper";
import { BodyTextM } from "../Typography";
import * as Styled from "./InvestmentProfilePage.styled";

type InvestmentProfilePageProps = DefaultStepProps & PensionQuestionnaireStepProps;

export const InvestmentProfilePage: FC<InvestmentProfilePageProps> = ({
    queries: { questionnaireConfigQuery, useAllowedChanges },
    contractId,
    setContinueCondition,
}) => {
    const {
        data: config,
        refetch: refetchQuestionnaireConfig,
        isLoading: isLoadingConfig,
    } = questionnaireConfigQuery ?? {};
    // fetch config if not available
    useEffect(() => {
        if (!config) refetchQuestionnaireConfig?.();
    }, [refetchQuestionnaireConfig, config]);

    const { t } = useTypedTranslation();
    const dispatch = useAppDispatch();
    const result = useAppSelector(editInvestmentStrategyResultSelector);
    const rejected = useAppSelector(questionnaireRejectedSelector);

    const { data: allowedChangesData } =
        useAllowedChanges?.({ id: contractId ?? "", options: { enabled: Boolean(contractId) } }) ?? {};
    const canChangeStrategy = useAppSelector(canChangeInvestmentStrategySelector) !== false;

    // Persist canChangeInvestmentStrategy to Redux so the recap page can use it
    useEffect(() => {
        if (allowedChangesData?.canChangeInvestmentStrategy !== undefined) {
            dispatch(setCanChangeInvestmentStrategy(allowedChangesData.canChangeInvestmentStrategy));
        }
    }, [allowedChangesData?.canChangeInvestmentStrategy, dispatch]);

    // When strategy change is disabled in ChangePensionContribution flow, skip to recap page
    useEffect(() => {
        if (!canChangeStrategy) {
            setContinueCondition(() => {
                dispatch(setStepByName(StepperIndexes.changeContributionRecapPage));
                return false;
            });
        }
    }, [canChangeStrategy, setContinueCondition, dispatch]);

    const data = useMemo(() => {
        const recommendedProfile = config?.investmentProfiles?.find((profile) => profile.id === result?.profileId);
        const recommendedStrategy = config?.recommendedStrategies?.find(
            (strategy) => strategy.id === result?.recommendedStrategyId,
        );
        const fundsWithAllocationRecommendations = config?.funds?.map((fund) => {
            const recommendedFund = recommendedStrategy?.funds.find((f) => f.id === fund.id);
            return {
                ...fund,
                recommendedPercentageAllocation: recommendedFund?.recommendedPercentageAllocation ?? 0,
            };
        });
        const recommendedFundId = recommendedStrategy?.funds?.find(
            (fund) => fund.recommendedPercentageAllocation > 60,
        )?.id;

        let title = recommendedProfile
            ? `${t("investmentStrategy.pensionQuestionnaire.investmentProfilePage.title1stPart")} ${recommendedProfile.name.toLowerCase()} ${t("investmentStrategy.pensionQuestionnaire.investmentProfilePage.title2ndPart")}`
            : null;
        if (rejected) {
            title = t("investmentStrategy.pensionQuestionnaire.investmentProfilePage.rejected");
        } else if (!title && !recommendedProfile) {
            title = `${t("investmentStrategy.pensionQuestionnaire.investmentProfilePage.titleIncorrectAnswers")}`;
        }
        const progressBarRows = fundsWithAllocationRecommendations?.map((fund) => ({
            id: fund.id,
            label: fund.name,
            value: fund.recommendedPercentageAllocation,
        }));

        return {
            recommendedProfile,
            recommendedStrategy,
            progressBarRows,
            recommendedFundId,
            title,
        };
    }, [config, result, t, rejected]);

    return (
        <Styled.InvestmentProfileWrapperStack>
            {/* Heading with result */}
            <Styled.InvestmentProfileHeadingXL>
                {isLoadingConfig ? <Skeleton variant="text" width="100%" /> : data.title}
            </Styled.InvestmentProfileHeadingXL>

            {rejected && !isLoadingConfig ? (
                <BodyTextM>
                    {t("investmentStrategy.pensionQuestionnaire.investmentProfilePage.rejectedMessage")}
                </BodyTextM>
            ) : (
                <>
                    {/* Profiles with selected recommended */}
                    <Styled.InvestmentProfileProfilesStack>
                        {config?.investmentProfiles.map((profile) => {
                            const selected = profile.id === data.recommendedProfile?.id;
                            return (
                                <Styled.InvestmentProfileProfilePaper
                                    key={profile.id}
                                    variant="outlined"
                                    $selected={selected}
                                >
                                    <Stack>
                                        <Styled.InvestmentProfileHeadingXS>
                                            {profile.name}
                                        </Styled.InvestmentProfileHeadingXS>
                                        <BodyTextM>{profile.description}</BodyTextM>
                                    </Stack>
                                </Styled.InvestmentProfileProfilePaper>
                            );
                        }) ??
                            Array.from({ length: 3 }, (_, index) => (
                                <Styled.InvestmentProfileProfilePaper key={index} variant="outlined">
                                    <Stack>
                                        <Styled.InvestmentProfileHeadingXS>
                                            <Skeleton variant="text" width="100%" />
                                        </Styled.InvestmentProfileHeadingXS>
                                        <BodyTextM>
                                            <Skeleton variant="text" width="100%" />
                                        </BodyTextM>
                                    </Stack>
                                </Styled.InvestmentProfileProfilePaper>
                            ))}
                    </Styled.InvestmentProfileProfilesStack>
                    {/* Result text */}
                    <Styled.InvestmentProfileTypography>
                        <Styled.BodyTextBold>
                            {t("investmentStrategy.pensionQuestionnaire.investmentProfilePage.comment")}{" "}
                        </Styled.BodyTextBold>
                        <BodyTextM>{result?.explanation ?? <Skeleton variant="text" width="100%" />}</BodyTextM>
                    </Styled.InvestmentProfileTypography>
                </>
            )}

            {/* Funds and recommended strategy wrapper */}
            <Styled.InvestmentProfileFundsAndStrategiesStack>
                <Styled.InvestmentProfileFundsStack>
                    <Styled.InvestmentProfileHeadingM>
                        {t("investmentStrategy.pensionQuestionnaire.investmentProfilePage.fundsOverview")}
                    </Styled.InvestmentProfileHeadingM>
                    {config?.funds?.map((fund) => {
                        const selected = fund.id === data.recommendedFundId;
                        return (
                            <Styled.InvestmentProfileFundPaper key={fund.id} $selected={selected} variant="outlined">
                                <Stack>
                                    <Styled.InvestmentProfileHeadingXS $selected={selected}>
                                        {fund.name}
                                        {selected &&
                                            ` (${t("investmentStrategy.pensionQuestionnaire.investmentProfilePage.recommended")})`}
                                    </Styled.InvestmentProfileHeadingXS>
                                    <BodyTextM>{fund.description}</BodyTextM>
                                </Stack>
                            </Styled.InvestmentProfileFundPaper>
                        );
                    }) ??
                        Array.from({ length: 3 }, (_, index) => (
                            <Styled.InvestmentProfileFundPaper key={index} variant="outlined">
                                <Stack>
                                    <Styled.InvestmentProfileHeadingXS>
                                        <Skeleton variant="text" width="100%" />
                                    </Styled.InvestmentProfileHeadingXS>
                                    <BodyTextM>
                                        <Skeleton variant="text" width="100%" />
                                    </BodyTextM>
                                </Stack>
                            </Styled.InvestmentProfileFundPaper>
                        ))}
                </Styled.InvestmentProfileFundsStack>

                <Styled.InvestmentProfileStrategiesStack>
                    <Styled.InvestmentProfileHeadingM>
                        {t("investmentStrategy.pensionQuestionnaire.investmentProfilePage.recommendedAllocation")}
                    </Styled.InvestmentProfileHeadingM>
                    <ProgressBarList rows={data.progressBarRows ?? []} isLoading={!data?.progressBarRows} />
                </Styled.InvestmentProfileStrategiesStack>
            </Styled.InvestmentProfileFundsAndStrategiesStack>

            {!canChangeStrategy && (
                <FormErrorMessage severity="warning">
                    {t("investmentStrategy.pensionQuestionnaire.investmentProfilePage.cannotChangeStrategyWarning")}
                </FormErrorMessage>
            )}
        </Styled.InvestmentProfileWrapperStack>
    );
};
